import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Filter, Loader2, Plus, Lightbulb } from "lucide-react";
import { campaignAPI } from "../services/api";

interface SegmentRow {
  id: string;
  name: string;
  definition?: string | Record<string, unknown>;
}

function parseRoomNumbersText(text: string): string[] {
  return text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatSegmentSummary(def: {
  room_prefix?: string;
  room_numbers?: string[];
}): string {
  const bits: string[] = [];
  if (def.room_prefix) {
    bits.push(`Prefix “${def.room_prefix}”`);
  }
  if (def.room_numbers?.length) {
    bits.push(`Rooms: ${def.room_numbers.join(", ")}`);
  }
  return bits.length ? bits.join(" · ") : "No room filter (invalid)";
}

const Segments: React.FC = () => {
  const [segments, setSegments] = useState<SegmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [roomPrefix, setRoomPrefix] = useState("");
  const [roomNumbersText, setRoomNumbersText] = useState("");
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [segmentIdeas, setSegmentIdeas] = useState<
    Array<{
      id: string;
      name: string;
      definition: { room_prefix?: string; room_numbers?: string[] };
      rationale: string;
    }>
  >([]);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const [data, sug] = await Promise.all([
        campaignAPI.listSegments(),
        campaignAPI.getSuggestions().catch(() => null),
      ]);
      setSegments(data);
      if (sug?.segments?.length) setSegmentIdeas(sug.segments);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const applySegmentIdea = (idea: (typeof segmentIdeas)[0]) => {
    setName(idea.name);
    setRoomPrefix(idea.definition?.room_prefix || "");
    setRoomNumbersText((idea.definition?.room_numbers || []).join(", "));
    const def: Record<string, unknown> = {};
    if (idea.definition?.room_prefix) def.room_prefix = idea.definition.room_prefix;
    if (idea.definition?.room_numbers?.length) def.room_numbers = idea.definition.room_numbers;
    if (Object.keys(def).length) {
      campaignAPI.previewSegment(def).then((r) => setPreviewCount(r.count)).catch(() => {});
    } else {
      setPreviewCount(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runPreview = async () => {
    const nums = parseRoomNumbersText(roomNumbersText);
    const prefix = roomPrefix.trim();
    if (!prefix && nums.length === 0) {
      setPreviewCount(null);
      return;
    }
    try {
      const def: Record<string, unknown> = {};
      if (prefix) def.room_prefix = prefix;
      if (nums.length) def.room_numbers = nums;
      const r = await campaignAPI.previewSegment(def);
      setPreviewCount(r.count);
    } catch (e) {
      console.error(e);
      setPreviewCount(null);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      runPreview();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomPrefix, roomNumbersText]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const nums = parseRoomNumbersText(roomNumbersText);
    const prefix = roomPrefix.trim();
    if (!prefix && nums.length === 0) {
      alert("Add a room prefix and/or at least one room number.");
      return;
    }
    setSaving(true);
    try {
      await campaignAPI.createSegment(name.trim(), {
        ...(prefix ? { room_prefix: prefix } : {}),
        ...(nums.length ? { room_numbers: nums } : {}),
      });
      setName("");
      setRoomPrefix("");
      setRoomNumbersText("");
      setPreviewCount(null);
      await load();
    } catch (err) {
      console.error(err);
      alert("Could not create segment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-full p-8 bg-slate-50">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/campaigns"
          className="inline-flex items-center gap-1 text-sm text-grey-guestby hover:text-black-guestby mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to campaigns
        </Link>

        <div className="flex items-center gap-2 mb-2">
          <Filter className="h-7 w-7 text-blue-guestby" />
          <h1 className="text-2xl font-bold text-black-guestby">Audience segments</h1>
        </div>
        <p className="text-grey-guestby text-sm mb-8">
          Segments filter checked-in guests who opted into marketing. Use a room number prefix (e.g.{" "}
          <code className="text-xs bg-slate-100 px-1 rounded">8</code> for floor 8), a list of specific
          rooms, or both (guests matching the prefix <span className="italic">or</span> in the list are
          included).
        </p>

        {isAdmin && segmentIdeas.length > 0 && (
          <div className="mb-8 rounded-xl border border-sky-200/80 bg-sky-50/90 p-5">
            <h2 className="text-sm font-semibold text-black-guestby flex items-center gap-2 mb-3">
              <Lightbulb className="h-4 w-4 text-sky-600" />
              Suggested segment definitions
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {segmentIdeas.map((idea) => (
                <div
                  key={idea.id}
                  className="rounded-lg border border-sky-200 bg-white p-3 text-left"
                >
                  <div className="font-medium text-black-guestby text-sm">{idea.name}</div>
                  <p className="text-xs text-slate-600 mt-1">{idea.rationale}</p>
                  {(idea.definition?.room_prefix || idea.definition?.room_numbers?.length) && (
                    <p className="text-xs text-slate-500 mt-1">
                      {formatSegmentSummary(idea.definition)}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => applySegmentIdea(idea)}
                    className="mt-2 text-xs font-medium text-blue-guestby hover:underline"
                  >
                    Use in form
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && (
          <form
            onSubmit={handleCreate}
            className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm mb-8 space-y-4"
          >
            <h2 className="text-sm font-semibold text-black-guestby">New segment</h2>
            <div>
              <label className="block text-xs text-grey-guestby mb-1">Name</label>
              <input
                required
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-black-guestby"
                placeholder="e.g. Executive floor"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-grey-guestby mb-1">Room prefix (optional)</label>
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-black-guestby"
                placeholder="e.g. 8 for rooms 801–899"
                value={roomPrefix}
                onChange={(e) => setRoomPrefix(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">
                Matches <code className="text-xs">room_number LIKE &quot;{roomPrefix || "…"}%&quot;</code>
              </p>
            </div>
            <div>
              <label className="block text-xs text-grey-guestby mb-1">
                Room numbers (optional)
              </label>
              <textarea
                rows={3}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-black-guestby text-sm font-mono"
                placeholder={"801, 802, 901\n(one per line or comma-separated)"}
                value={roomNumbersText}
                onChange={(e) => setRoomNumbersText(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">
                Exact room matches. Combined with prefix using OR — include guests in the list or matching
                the prefix.
              </p>
            </div>
            {previewCount !== null && (
              <p className="text-sm text-black-guestby">
                <span className="font-medium">{previewCount}</span> checked-in guests match (marketing
                opt-in only).
              </p>
            )}
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-guestby text-black-guestby font-medium"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Save segment
            </button>
          </form>
        )}

        <h2 className="text-sm font-semibold text-black-guestby mb-3">Saved segments</h2>
        {loading ? (
          <div className="flex justify-center py-12 text-grey-guestby">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : segments.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-grey-guestby text-sm">
            No saved segments yet. Use a suggestion above or create one in the form — demo data also seeds
            common floor segments when the demo hotel exists.
          </div>
        ) : (
          <ul className="space-y-2">
            {segments.map((s) => {
              let def: { room_prefix?: string; room_numbers?: string[] } = {};
              try {
                const raw = s.definition;
                def =
                  typeof raw === "string"
                    ? JSON.parse(raw || "{}")
                    : (raw as { room_prefix?: string; room_numbers?: string[] }) || {};
              } catch {
                def = {};
              }
              return (
                <li
                  key={s.id}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-3 flex justify-between items-start gap-4"
                >
                  <span className="font-medium text-black-guestby">{s.name}</span>
                  <span className="text-sm text-grey-guestby text-right max-w-[60%]">
                    {formatSegmentSummary(def)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Segments;
